// src/services/looker/lookerApi.ts
/**
 * Browser side of /api/looker-*. Sends the caller's Supabase token; the
 * server decides what that caller may see. Never throws.
 *
 * @module services/looker/lookerApi
 */
import { getSupabaseClient } from '@/config/supabase';
import { logError } from '@/services/logger';
import type { LookerDashboardPayload, LookerScope, LookerTemplateSummary, LookerTileImage } from './lookerTypes';
import type { TemplateSpec } from './lookerTemplateSpec';

/** `error` is the server's own message when it sent one (a 400 says what to fix). */
export type LookerFetchResult<T> = { ok: true; data: T } | { ok: false; status: number; error?: string };

/** The server's message from a failed answer, if it is JSON with one. */
const errorOf = async (resp: Response): Promise<string | undefined> => {
  try {
    const j = (await resp.json()) as { error?: unknown; detail?: unknown };
    const text = typeof j.detail === 'string' ? j.detail : j.error;
    return typeof text === 'string' ? text : undefined;
  } catch {
    return undefined;
  }
};

const authHeader = async (): Promise<Record<string, string> | null> => {
  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : null;
};

const call = async <T>(path: string, init: RequestInit, pick: (j: unknown) => T): Promise<LookerFetchResult<T>> => {
  try {
    const auth = await authHeader();
    if (!auth) return { ok: false, status: 401 };
    const resp = await fetch(path, { ...init, headers: { ...(init.headers ?? {}), ...auth } });
    if (!resp.ok) return { ok: false, status: resp.status, error: await errorOf(resp) };
    return { ok: true, data: pick(await resp.json()) };
  } catch (err) {
    logError('LookerApi', `${path} failed`, err);
    return { ok: false, status: 0 };
  }
};

/** The templates this scope has: a workspace's default and grants, or every one for all workspaces. */
export const fetchLookerTemplates = (scope: LookerScope): Promise<LookerFetchResult<LookerTemplateSummary[]>> => {
  const query = scope.kind === 'all' ? 'all=1' : `workspaceId=${encodeURIComponent(scope.workspaceId)}`;
  return call(`/api/looker-templates?${query}`, { method: 'GET' }, (j) => (j as { templates: LookerTemplateSummary[] }).templates);
};

export const fetchLookerDashboard = (req: {
  templateId: string;
  scope: LookerScope;
  filters: Record<string, string>;
  /** The app's channel selection; [] = every channel. */
  channels?: string[];
  /** The viewer's currency pick; the server forces it only when the scope has it (FR-X-02). */
  currency?: string;
}): Promise<LookerFetchResult<LookerDashboardPayload>> =>
  call('/api/looker-dashboard', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  }, (j) => j as LookerDashboardPayload);

/** What a tile image is asked with: the same template, scope, filters and channels as its numbers. */
export interface LookerImageRequest {
  templateId: string;
  scope: LookerScope;
  filters: Record<string, string>;
  channels?: string[];
  /** The currency the tile's numbers are in; the server checks it against the scope's list again. */
  currency?: string;
}

/** Looker's picture of one tile the app does not draw. Slow: Looker renders it (seconds). */
export const fetchLookerTileImage = (req: LookerImageRequest & { tileId: string; width: number }): Promise<LookerFetchResult<LookerTileImage>> =>
  call('/api/looker-tile-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  }, (j) => j as LookerTileImage);

/** /api/looker-admin: master-only writes to a template in the template folder. */
const admin = <T>(body: Record<string, unknown>): Promise<LookerFetchResult<T>> =>
  call('/api/looker-admin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, (j) => j as T);

/** A sent description (even '') replaces the old one; leave it out to keep it. */
export const renameLookerTemplate = (templateId: string, title: string, description?: string) =>
  admin<{ id: string; title: string; description: string | null }>({ action: 'rename', templateId, title, description });

/** The copy always lands in the template folder; the server names it "… (copy)". */
export const copyLookerTemplate = (templateId: string) =>
  admin<{ id: string; title: string }>({ action: 'copy', templateId });

/** Moves it to Looker's Trash and removes its grants. 409 for the default template. */
export const deleteLookerTemplate = (templateId: string) =>
  admin<{ deleted: true; revokedGrants: number | null }>({ action: 'delete', templateId });

/** A field of the explore the builder may use; bare name, as a spec names it. */
export interface LookerBuilderField {
  name: string;
  label: string;
  kind: 'dimension' | 'measure';
  type: string;
}

export const fetchLookerBuilderFields = () =>
  admin<{ fields: LookerBuilderField[] }>({ action: 'fields' });

/** The spec run on real data in the /looker payload shape. Writes nothing to Looker. */
export const previewLookerTemplate = (spec: TemplateSpec, scope: LookerScope) =>
  admin<LookerDashboardPayload>({ action: 'preview', spec, scope });

/** A new template in the template folder, editable later in the builder. */
export const createLookerTemplate = (spec: TemplateSpec) =>
  admin<{ id: string; title: string }>({ action: 'create', spec });

/** 409 when the template was not made in the app. */
export const updateLookerTemplate = (templateId: string, spec: TemplateSpec) =>
  admin<{ id: string; title: string }>({ action: 'update', templateId, spec });
